/**
 * Mock payload builders for the dev-only mock API (see ./mockApi.js).
 *
 * Everything here fabricates the a8 API response shapes the app consumes, with
 * every field populated so the full UI can be exercised offline:
 *   - placeholder images are inline SVG data-URIs (solid color blocks + label),
 *     so no network and no canvas-tainting (safe for the scratcher's
 *     getImageData reveal detection)
 *   - copy is lorem ipsum
 *
 * NOT BUNDLED IN PRODUCTION — only imported from mockApi.js, which is only
 * required behind a `process.env.NODE_ENV === 'development'` guard.
 */

/* eslint-disable import/no-anonymous-default-export */

// The real scratcher artwork bundled with the scratcher package (McDonald's
// "SCRATCH TO REVEAL"). Used as the default mock overlay so the dev experience
// matches production-quality art.
import scratcherArt from '../components/Scratcher/core/assets/scratcher-bg.jpg';

// ---------------------------------------------------------------------------
// Placeholder images — solid color blocks with a label, as SVG data-URIs.
// ---------------------------------------------------------------------------

export const placeholderImage = (label, w, h, bg, fg = '#FFFFFF') => {
  const fontSize = Math.round(Math.min(w, h) / 7);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<rect width="100%" height="100%" fill="${bg}"/>` +
    `<rect x="8" y="8" width="${w - 16}" height="${h - 16}" fill="none" stroke="${fg}" stroke-opacity="0.35" stroke-width="2" stroke-dasharray="10 8"/>` +
    `<text x="50%" y="50%" fill="${fg}" font-family="system-ui, -apple-system, sans-serif" font-size="${fontSize}" font-weight="700" text-anchor="middle" dominant-baseline="middle">${label}</text>` +
    `<text x="50%" y="${h - fontSize}" fill="${fg}" fill-opacity="0.7" font-family="system-ui, -apple-system, sans-serif" font-size="${Math.round(fontSize * 0.45)}" text-anchor="middle">${w}×${h} · mock</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const IMAGES = {
  hero: placeholderImage('HERO IMAGE', 828, 414, '#3F51B5'),
  thumbnail: placeholderImage('THUMB', 200, 200, '#00897B'),
  inBody: placeholderImage('IN-BODY IMAGE', 828, 310, '#8E24AA'),
  overlay: placeholderImage('SCRATCH HERE', 828, 1200, '#E8A030', '#4A1A7A'),
};

// ---------------------------------------------------------------------------
// Lorem ipsum copy.
// ---------------------------------------------------------------------------

const LOREM_DESCRIPTION = `
  <p><strong>Lorem ipsum dolor sit amet</strong>, consectetur adipiscing elit. Integer
  posuere erat a ante venenatis dapibus posuere velit aliquet. Donec ullamcorper nulla
  non metus auctor fringilla.</p>
  <ul>
    <li>Curabitur blandit tempus porttitor</li>
    <li>Nullam quis risus eget urna mollis ornare</li>
    <li>Maecenas faucibus mollis interdum</li>
  </ul>
  <p>Aenean lacinia bibendum nulla sed consectetur. Visit
  <a href="https://example.com" target="_blank" rel="noreferrer">example.com</a> for
  more lorem.</p>
`;

const LOREM_TERMS = `
  <h3>Mock Terms &amp; Conditions</h3>
  <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum id ligula
  porta felis euismod semper. Cras justo odio, dapibus ac facilisis in, egestas eget
  quam. Limit one per customer. Void where prohibited. While supplies last.</p>
  <p>Sed posuere consectetur est at lobortis. Donec id elit non mi porta gravida at
  eget metus. T&amp;Cs apply.</p>
`;

// Sensible default code values per barcode format (some formats are digit/length-strict).
// All are literal redemption codes — the displayed code should read like a code
// (e.g. "QRMOCK12345"), never a URL.
const BARCODE_VALUES = {
  QRCode: 'QRMOCK12345',
  Code128: '1234567890',
  Code39: 'MOCK12345',
  'EAN-8': '9031101',
  'EAN-13': '590123412345',
  'UPC-E': '01234565',
  'UPC-A': '01234567890',
  ITF14: '0501234567890',
  PDF417: 'MOCK-1234567890',
  Aztec: 'MOCK-1234567890',
  DataMatrix: 'MOCK-1234567890',
};

// "YYYY-MM-DD HH:mm:ss" in UTC — the shape utcDateConverter()/moment expect.
const dateString = (offsetDays) =>
  new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 19).replace('T', ' ');

// ---------------------------------------------------------------------------
// Content builder.
// ---------------------------------------------------------------------------

/**
 * Build a complete mock content object of the given type with ALL fields the
 * renderers read. `opts` (parsed from mock_* URL params in mockApi.js):
 *   scratcher  bool   scratch-off overlay (default: true for offer/prize)
 *   overlay    'art' (default) = bundled McDonald's scratch artwork;
 *              'block' = SVG color-block placeholder;
 *              'fallback' = omit overlay url to exercise Content.js's bundled
 *              swipe-to-reveal fallback image
 *   timer      bool   redemption countdown timer (default true)
 *   ageGate    bool   21+ age gate (default false)
 *   terms      bool   terms & conditions link/modal (default true)
 *   barcode    string barcode format (default 'Code128')
 *   method     1 | 2 | 'download'  redemption method (default 2 = in-store)
 *   passes     bool   apple/google wallet passes (default false — needs gapi for Google)
 *   clickThrough bool add click_through_btn_1/2 (default false)
 *   qrScanner  bool   add open_qr_scanner_btn (default false)
 *   tags       string[] tag names, e.g. ['auto-save', 'no-action-buttons']
 *   expired    bool   put end_at in the past (default false)
 */
export const buildMockContent = (type, opts = {}) => {
  const {
    scratcher = type === 'offer' || type === 'prize',
    overlay = 'art',
    timer = true,
    ageGate = false,
    terms = true,
    barcode = 'Code128',
    method = 2,
    passes = false,
    clickThrough = false,
    qrScanner = false,
    tags = [],
    expired = false,
  } = opts;

  const isDownload = method === 'download';
  const redemptionUrl = isDownload
    ? `https://example.com/get-app?action=download&ios=${encodeURIComponent(
        'https://apps.apple.com/app/id000000000'
      )}&android=${encodeURIComponent(
        'https://play.google.com/store/apps/details?id=com.example.mock'
      )}`
    : 'https://example.com/mock-redeem-online';

  const buttons = [
    { key: 'redeem_in_store_btn', label: 'Redeem In-Store (mock)', locale: 'en' },
    { key: 'redeem_online_btn', label: 'Buy Online (mock)', locale: 'en' },
  ];
  if (clickThrough) {
    buttons.push(
      { key: 'click_through_btn_1', label: 'Click-Through One', locale: 'en', value: 'https://example.com/ct-1' },
      { key: 'click_through_btn_2', label: 'Click-Through Two', locale: 'en', value: 'https://example.com/ct-2' }
    );
  }
  if (qrScanner) {
    buttons.push({ key: 'open_qr_scanner_btn', label: 'Scan QR Code', locale: 'en' });
  }

  const content = {
    id: 101,
    client_id: 1,
    type,
    display_name: `Lorem Ipsum ${type.charAt(0).toUpperCase() + type.slice(1)} — Dolor Sit Amet 20% Off`,
    brand: 'Mock Brand Co.',
    description: LOREM_DESCRIPTION,
    end_at: dateString(expired ? -2 : 14),
    age_gate_enabled: ageGate,
    content_term_id: terms ? 7 : null,
    apple_pass_enabled: passes,
    android_pass_enabled: passes,
    tags: tags.map((name) => ({ name })),
    buttons,
    pivot: { campaign_id: 11, trigger_id: 22, content_id: 101 },
    images: {
      hero: { url: IMAGES.hero },
      thumbnail: { url: IMAGES.thumbnail },
      in_body: type === 'message' ? { url: IMAGES.inBody } : null,
    },
    scratcher: scratcher
      ? {
          enabled: true,
          images:
            overlay === 'fallback'
              ? null
              : { overlay: { url: overlay === 'block' ? IMAGES.overlay : scratcherArt } },
        }
      : { enabled: false },
    redemption: null,
    url: null,
    urls: null,
  };

  if (type === 'offer' || type === 'prize') {
    content.redemption = {
      method: isDownload ? 1 : Number(method),
      url: redemptionUrl,
      code: {
        value: BARCODE_VALUES[barcode] ?? BARCODE_VALUES.Code128,
        format: barcode,
      },
      timer: { enabled: timer, limit: 120 },
      status: { hide_at: null },
    };
  }

  if (type === 'url' || type === 'message') {
    content.url = 'https://example.com/mock-click-here';
    content.urls = [
      { url: 'https://example.com/mock-link-one', label: 'Visit Lorem One', target: '_blank' },
      { url: 'https://example.com/mock-link-two', label: 'Visit Ipsum Two', target: '_self' },
    ];
    // mock_redirect: url content whose target carries a8_redirect=1 → the app
    // auto-redirects and shows the interstitial. 'stay' targets a same-page
    // hash, so the navigation is a no-op and the interstitial stays visible.
    if (type === 'url' && opts.redirect) {
      content.url =
        opts.redirect === 'stay'
          ? `${window.location.href.split('#')[0]}#a8_redirect=1`
          : 'https://example.com/sample-redirect-target?a8_redirect=1';
    }
  }

  return content;
};

// ---------------------------------------------------------------------------
// Other endpoint payloads.
// ---------------------------------------------------------------------------

export const mockDevice = (uuid) => ({
  id: 424242,
  uuid: uuid || 'mock-device-uuid',
  locale: 'en-us',
});

export const mockTermsBody = () => ({ body: LOREM_TERMS });

// Saved/clicked wallet items. Shaped like the content list the backend returns
// for `/device/{uuid}/content?status=[2,3,4,5]` — id, display_name, brand,
// status, and images.thumbnail so the wallet cards have a thumbnail to show.
export const mockWalletItems = () => [
  {
    id: 101,
    display_name: 'Lorem Ipsum Offer — 20% Off Everything',
    brand: 'Mock Brand Co.',
    status: 2,
    images: { thumbnail: { url: IMAGES.thumbnail }, hero: { url: IMAGES.hero } },
  },
  {
    id: 102,
    display_name: 'Dolor Sit Amet — Buy One Get One',
    brand: 'Ipsum Retail',
    status: 5,
    images: { thumbnail: { url: placeholderImage('THUMB', 200, 200, '#5E35B1') } },
  },
  {
    id: 103,
    display_name: 'Consectetur Rewards Member Deal',
    brand: 'Adipiscing Goods',
    status: 4,
    images: { thumbnail: { url: placeholderImage('THUMB', 200, 200, '#C2185B') } },
  },
];

export const mockAndroidPass = () => ({ jwt: { jwt: 'mock.jwt.token-not-real' } });
